import { View, Text, StyleSheet, Font } from "@react-pdf/renderer";
import regular from "@packages/assets/font-family/Montserrat-Regular.ttf";
import bold from "@packages/assets/font-family/Montserrat-Bold.ttf";
import { useTranslation } from "react-i18next";
import { PdfLayout } from "../pdf/PdfLayout";
import { commonStyles } from "../pdf/CommonPdfStyles";
import MarkdownRenderer from "../custom-markdown";
import { AiHomeworkIdeaCardProps } from "./AiHomeworkIdeaCard";
import { scaleFontSize } from "../pdf/fontSizeAdjustment";
Font.register({
  family: "Montserrat",
  fonts: [{ src: regular }, { src: bold, fontWeight: "bold" }],
});

// Helper to get complexity level colors for PDF
const getComplexityPdfStyle = (level: string) => {
  // Check for exact match first, then partial includes to be more precise
  if (level === "BEGINNER") {
    return {
      backgroundColor: "#dcfce7",
      color: "#166534",
      borderColor: "#22c55e",
      titleColor: "#166534",
    };
  } else if (level === "INTERMEDIATE") {
    return {
      backgroundColor: "#fef3c7",
      color: "#92400e",
      borderColor: "#f59e0b",
      titleColor: "#92400e",
    };
  } else if (level === "ADVANCED") {
    return {
      backgroundColor: "#fee2e2",
      color: "#b91c1c",
      borderColor: "#ef4444",
      titleColor: "#b91c1c",
    };
  }

  // Default teal colors
  return {
    backgroundColor: "#ccfbf1",
    color: "#0f766e",
    borderColor: "#14b8a6",
    titleColor: "#0f766e",
  };
};

const styles = StyleSheet.create({
  ideaContainer: {
    marginBottom: 15,
    padding: 12,
    backgroundColor: "#f0fdf4",
    borderRadius: 8,
  },
  ideaTitle: {
    fontSize: 14,
    fontWeight: "bold",
    marginBottom: 8,
  },
  complexityBadge: {
    alignSelf: "flex-start",
    padding: "4 8",
    borderRadius: 4,
    fontSize: 9,
  },
  description: {
    fontSize: 10,
    color: "#374151",
    lineHeight: 1.6,
    marginBottom: 10,
  },
  resourceItem: {
    flexDirection: "row",
    marginBottom: 6,
    paddingLeft: 8,
  },
  bullet: {
    width: 12,
    fontSize: 11,
  },
  sectionTitle: {
    fontSize: 12,
    fontWeight: "bold",
    marginTop: 12,
    marginBottom: 8,
  },
  sectionContainer: {
    borderLeftWidth: 4,
    paddingLeft: 8,
    marginBottom: 12,
  },
});

const AiHomeworkIdeaDocument = ({
  homeworkIdea,
  customizationSettings,
}: AiHomeworkIdeaCardProps) => {
  const { t } = useTranslation();

  // Handle backward compatibility - use reportIdeas if available, otherwise fall back to ideas
  const ideas = homeworkIdea.reportIdeas || homeworkIdea.ideas || [];

  // Get complexity style for the first idea to use for header color
  const firstIdeaComplexity = ideas[0]?.complexity_level;
  const headerColor = firstIdeaComplexity
    ? getComplexityPdfStyle(firstIdeaComplexity).titleColor
    : "#16a34a";

  return (
    <PdfLayout
      courseName={homeworkIdea.subject}
      gradeLevel={homeworkIdea.gradeLevel}
      topic={homeworkIdea.title || homeworkIdea.topic}
      imageUrl={homeworkIdea.imageUrl}
      headerTextColor={headerColor}
      customizationSettings={customizationSettings}
      createdDate={homeworkIdea.createdAt}
      author={homeworkIdea.authorName}
    >
      {ideas.map((idea, index) => {
        // Try to get complexity from the field or from the translation
        const translatedComplexity = t(
          `aiHomeworkIdeas.complexity.${idea.complexity_level}`,
        );
        // Get complexity style for this idea
        const complexityStyle = getComplexityPdfStyle(idea.complexity_level);

        return (
          <View
            key={index}
            style={[
              styles.ideaContainer,
              { backgroundColor: complexityStyle.backgroundColor + "33" },
            ]}
          >
            <MarkdownRenderer
              customStyle={{
                text: {
                  text: {
                    ...styles.ideaTitle,
                    fontSize: scaleFontSize(
                      styles.ideaTitle.fontSize,
                      customizationSettings?.fontSize,
                    ),
                    color: complexityStyle.titleColor,
                  },
                  container: {
                    marginBottom: 8,
                  },
                },
              }}
              content={idea.title}
            />
            <Text
              style={[
                styles.complexityBadge,
                getComplexityPdfStyle(idea.complexity_level),
                {
                  fontSize: scaleFontSize(
                    styles.complexityBadge.fontSize,
                    customizationSettings?.fontSize,
                  ),
                },
              ]}
            >
              {translatedComplexity}
            </Text>

            {/* Assignment Description Section */}
            <Text
              style={[
                styles.sectionTitle,
                {
                  fontSize: scaleFontSize(
                    styles.sectionTitle.fontSize,
                    customizationSettings?.fontSize,
                  ),
                  color: complexityStyle.titleColor,
                },
              ]}
            >
              {t("aiHomeworkIdeas.assignmentDescription")}
            </Text>
            <View
              style={[
                styles.sectionContainer,
                { borderLeftColor: complexityStyle.borderColor },
              ]}
            >
              <MarkdownRenderer
                customStyle={{
                  text: {
                    text: {
                      ...styles.description,
                      fontSize: scaleFontSize(
                        styles.description.fontSize,
                        customizationSettings?.fontSize,
                      ),
                    },
                    container: {
                      marginBottom: 12,
                      paddingVertical: 2,
                    },
                  },
                }}
                content={idea.description}
              />
            </View>

            {/* Why AI-Resistant Section */}
            <Text
              style={[
                styles.sectionTitle,
                {
                  fontSize: scaleFontSize(
                    styles.sectionTitle.fontSize,
                    customizationSettings?.fontSize,
                  ),
                  color: complexityStyle.titleColor,
                },
              ]}
            >
              {t("aiHomeworkIdeas.whyAiResistant")}
            </Text>
            <View
              style={[
                styles.sectionContainer,
                { borderLeftColor: complexityStyle.borderColor },
              ]}
            >
              <MarkdownRenderer
                customStyle={{
                  text: {
                    text: {
                      ...styles.description,
                      fontSize: scaleFontSize(
                        styles.description.fontSize,
                        customizationSettings?.fontSize,
                      ),
                    },
                    container: {
                      marginBottom: 12,
                      paddingVertical: 2,
                    },
                  },
                  listItem: {
                    text: {
                      ...styles.description,
                      fontSize: scaleFontSize(
                        styles.description.fontSize,
                        customizationSettings?.fontSize,
                      ),
                    },
                    container: {
                      marginBottom: 12,
                      paddingVertical: 2,
                    },
                  },
                }}
                content={idea.explanation}
              />
            </View>

            {idea.outline && (
              <>
                <Text
                  style={[
                    styles.sectionTitle,
                    {
                      fontSize: scaleFontSize(
                        styles.sectionTitle.fontSize,
                        customizationSettings?.fontSize,
                      ),
                      color: complexityStyle.titleColor,
                    },
                  ]}
                >
                  {t("aiHomeworkIdeas.outline")}
                </Text>
                <View
                  style={[
                    styles.sectionContainer,
                    { borderLeftColor: complexityStyle.borderColor },
                  ]}
                >
                  <MarkdownRenderer
                    customStyle={{
                      text: {
                        text: {
                          ...commonStyles.sectionContent,
                          fontSize: scaleFontSize(
                            commonStyles.sectionContent.fontSize,
                            customizationSettings?.fontSize,
                          ),
                          color: "#374151",
                        },
                        container: {
                          ...commonStyles.sectionContent,
                          marginBottom: 12,
                          paddingVertical: 2,
                        },
                      },
                    }}
                    content={idea.outline}
                  />
                </View>
              </>
            )}

            {idea.recommended_resources && (
              <>
                <Text
                  style={[
                    styles.sectionTitle,
                    {
                      fontSize: scaleFontSize(
                        styles.sectionTitle.fontSize,
                        customizationSettings?.fontSize,
                      ),
                      color: complexityStyle.titleColor,
                    },
                  ]}
                >
                  {t("aiHomeworkIdeas.resources")}
                </Text>
                <View
                  style={[
                    styles.sectionContainer,
                    { borderLeftColor: complexityStyle.borderColor },
                  ]}
                >
                  {idea.recommended_resources.map((resource, idx) => (
                    <View
                      key={idx}
                      style={[styles.resourceItem, { marginBottom: 10 }]}
                    >
                      <Text
                        style={[
                          styles.bullet,
                          { color: complexityStyle.titleColor },
                        ]}
                      >
                        •
                      </Text>
                      <MarkdownRenderer
                        customStyle={{
                          text: {
                            text: {
                              ...styles.description,
                              fontSize: scaleFontSize(
                                styles.description.fontSize,
                                customizationSettings?.fontSize,
                              ),
                            },
                            container: {
                              marginBottom: 0,
                            },
                          },
                          listItem: {
                            text: {
                              ...styles.description,
                              fontSize: scaleFontSize(
                                styles.description.fontSize,
                                customizationSettings?.fontSize,
                              ),
                            },
                            container: {
                              marginBottom: 0,
                            },
                          },
                        }}
                        content={resource}
                      />
                    </View>
                  ))}
                </View>
              </>
            )}
          </View>
        );
      })}
    </PdfLayout>
  );
};

function AiHomeworkIdeaCardPDF(props: AiHomeworkIdeaCardProps) {
  return <AiHomeworkIdeaDocument {...props} />;
}

export default AiHomeworkIdeaCardPDF;
